import type { AuthExtras, WorkspaceAuthScope } from "../types/auth";
import type { DataSource } from "@firecms/core";
import {
  fetchAccountUserMembership,
  fetchEventUserMembership,
} from "../firebase/membershipQueries";
import {
  fetchEventRuntimeState,
  isEventProvisioningReady,
  provisioningDenialMessage,
} from "../collections/eventProvisioning";
import type { AdminMode } from "../workspace/adminMode";
import { canAccessMode } from "../workspace/adminMode";
import {
  getEventManagerAccessDeniedMessage,
  type PanelAccessDenialReason,
} from "../workspace/eventAccessMessages";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";

export type PanelAccessOutcome = "granted" | "denied";

export type PanelAccessResult = {
  outcome: PanelAccessOutcome;
  denialMessage?: string;
  denialReason?: PanelAccessDenialReason;
  workspace?: WorkspaceAuthScope;
};

type DataSourceFetcher = Pick<DataSource, "fetchEntity">;

const accountWorkspace = (
  accountId: string,
  roles: Pick<WorkspaceAuthScope, "isAccountManager" | "isEventManager">
): WorkspaceAuthScope => ({
  accountId,
  ...roles,
});

const eventWorkspace = (
  accountId: string,
  eventId: string,
  roles: Pick<WorkspaceAuthScope, "isAccountManager" | "isEventManager">
): WorkspaceAuthScope => ({
  accountId,
  eventId,
  ...roles,
});

export async function verifyPanelAccess(
  dataSource: DataSourceFetcher,
  userId: string,
  mode: AdminMode,
  extra: AuthExtras
): Promise<PanelAccessResult> {
  if (!canAccessMode(mode, extra)) {
    return { outcome: "denied" };
  }

  if (mode.kind === "global") {
    return extra.isSuperManager
      ? { outcome: "granted" }
      : { outcome: "denied" };
  }

  if (mode.kind === "hub") {
    return { outcome: "denied" };
  }

  if (extra.isSuperManager) {
    if (mode.kind === "event") {
      const locale = getAdminUiLocale();
      const runtime = await fetchEventRuntimeState(
        dataSource,
        mode.id.accountId,
        mode.id.eventId
      );
      if (!isEventProvisioningReady(runtime?.status)) {
        return {
          outcome: "denied",
          denialMessage: provisioningDenialMessage(runtime, locale),
          denialReason: "provisioning",
        };
      }
      return {
        outcome: "granted",
        workspace: eventWorkspace(mode.id.accountId, mode.id.eventId, {
          isAccountManager: true,
          isEventManager: true,
        }),
      };
    }
    return {
      outcome: "granted",
      workspace: accountWorkspace(mode.id.accountId, {
        isAccountManager: true,
        isEventManager: false,
      }),
    };
  }

  if (mode.kind === "account" || mode.kind === "space") {
    const allowed = await fetchAccountUserMembership(
      dataSource,
      userId,
      mode.id.accountId
    );
    if (!allowed) {
      return { outcome: "denied" };
    }
    return {
      outcome: "granted",
      workspace: accountWorkspace(mode.id.accountId, {
        isAccountManager: true,
        isEventManager: false,
      }),
    };
  }

  if (mode.kind === "event") {
    const { accountId, eventId } = mode.id;
    const [accountManager, eventManager] = await Promise.all([
      fetchAccountUserMembership(dataSource, userId, accountId),
      fetchEventUserMembership(dataSource, userId, accountId, eventId),
    ]);

    if (!accountManager && !eventManager) {
      const locale = getAdminUiLocale();
      return {
        outcome: "denied",
        denialMessage: getEventManagerAccessDeniedMessage(locale),
        denialReason: "event_access",
      };
    }

    const locale = getAdminUiLocale();
    const runtime = await fetchEventRuntimeState(dataSource, accountId, eventId);
    if (!isEventProvisioningReady(runtime?.status)) {
      return {
        outcome: "denied",
        denialMessage: provisioningDenialMessage(runtime, locale),
        denialReason: "provisioning",
      };
    }
    return {
      outcome: "granted",
      workspace: eventWorkspace(accountId, eventId, {
        isAccountManager: accountManager,
        isEventManager: eventManager,
      }),
    };
  }

  return { outcome: "denied" };
}
